import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Alert, Avatar, Stack, Chip,
  Typography, InputAdornment, TablePagination, MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Class as ClassIcon, Search as SearchIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon,
  ToggleOn as ActiveIcon, ToggleOff as InactiveIcon,
} from '@mui/icons-material';
import { Switch, FormControlLabel } from '@mui/material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const blank = { class: '', class_t: '', is_active: 1, next_class: '' };

export default function ClassesPage() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [open, setOpen]     = useState(false);
  const [editing, setEdit]  = useState(null);
  const [form, setForm]     = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [q, setQ]           = useState('');
  const [page, setPage]     = useState(0);
  const [perPage, setPP]    = useState(25);

  const load = async () => {
    setLoad(true);
    try { setRows((await API.get('/classes')).data); }
    catch (e) { setError(e.response?.data?.message || 'Failed to load'); }
    finally { setLoad(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setEdit(null); setForm(blank); setError(''); setOpen(true); };
  const openEdit = (r) => {
    setEdit(r);
    setForm({
      class: r.class,
      class_t: r.class_t || '',
      is_active: r.is_active ?? 1,
      next_class: r.next_class || '',
    });
    setError(''); setOpen(true);
  };

  const submit = async () => {
    if (!form.class.trim()) { setError('Class name required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await API.put(`/classes/${editing.trans_code}`, form);
        setOpen(false);
        swal.success('Class updated');
      } else {
        await API.post('/classes', form);
        setForm(blank);   // ready for the next class
        swal.success('Class created — add another or close');
      }
      await load();
    } catch (e) { setError(swal.pickError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    // Say the count out loud before asking. The server refuses a class
    // that still has learners in it, but being told "26 learners" before
    // you confirm is better than being told after — and it explains the
    // refusal that would follow, so it does not read as a bug.
    const n = Number(r.student_count) || 0;
    if (n > 0) {
      return swal.warning(
        `"${r.class}" still has ${n} learner${n === 1 ? '' : 's'} in it. Move them to `
        + 'another class first, or use the Active toggle to hide this class instead — '
        + 'that keeps everybody where they are.',
        'Cannot delete a class with learners'
      );
    }
    const ok = await swal.confirm({
      title: `Delete "${r.class}"?`,
      text: r.student_count == null
        ? 'The learner count could not be read for this class, so it may not be empty. '
          + 'Reload the page before deleting.'
        : 'This class is empty, so nobody is affected. It will be removed from the class '
          + 'list and the promotion order.',
      tone: 'danger',
      confirmText: 'Yes, delete it',
    });
    if (!ok) return;
    try {
      const resp = await API.delete(`/classes/${r.trans_code}`);
      swal.success(resp.data?.message || 'Class deleted');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  // Move a class up or down within its active bucket. Swaps in the
  // local array (instant visual response), then POSTs the full new
  // order to the backend which restamps sort_order = 10, 20, 30…
  // Zero fragile neighbour-lookup logic — the client decides the
  // order, the backend just writes it down. Rolls back on failure.
  const move = async (r, direction) => {
    // Compute the new full ordering BEFORE calling setRows so we can
    // reuse the same array for the API call.
    const current = rows;
    const idx = current.findIndex(x => x.trans_code === r.trans_code);
    if (idx < 0) return;
    let swapIdx = -1;
    if (direction === 'up') {
      for (let i = idx - 1; i >= 0; i--) {
        if (!!current[i].is_active === !!r.is_active) { swapIdx = i; break; }
      }
    } else {
      for (let i = idx + 1; i < current.length; i++) {
        if (!!current[i].is_active === !!r.is_active) { swapIdx = i; break; }
      }
    }
    if (swapIdx < 0) return;   // already at the edge

    const nextOrder = [...current];
    [nextOrder[idx], nextOrder[swapIdx]] = [nextOrder[swapIdx], nextOrder[idx]];
    setRows(nextOrder);   // optimistic

    try {
      // Only send the ACTIVE bucket — inactive rows keep whatever
      // sort_order they already have (they don't participate in the
      // ordering anyway).
      const orderIds = nextOrder.filter(x => x.is_active).map(x => x.trans_code);
      await API.post('/classes/reorder', { order: orderIds });
    } catch (e) {
      swal.error(swal.pickError(e, 'Move failed'));
      await load();   // snap back to truth
    }
  };

  // Flip active/inactive from the row's toggle. Inactive classes fall
  // to the bottom of the list and are excluded from the Examination
  // Promotion "next class" order.
  const toggleActive = async (r) => {
    try {
      await API.put(`/classes/${r.trans_code}`, {
        class: r.class, class_t: r.class_t, type: r.type,
        is_active: r.is_active ? 0 : 1,
      });
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Toggle failed')); }
  };

  const stats = useMemo(() => ({
    total:        rows.length,
    active:       rows.filter(r => r.is_active).length,
    withTeacher:  rows.filter(r => r.class_t && r.class_t !== 'N/A' && r.class_t.trim() !== '').length,
    // Classes with nobody in them — the ones that can be deleted, which
    // is the whole reason the count is on this page.
    empty:        rows.filter(r => r.student_count === 0).length,
    learners:     rows.reduce((s, r) => s + (Number(r.student_count) || 0), 0),
  }), [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    const query = q.trim().toLowerCase();
    if (!query) return true;
    const blob = `${r.class || ''} ${r.class_t || ''}`.toLowerCase();
    return blob.includes(query);
  }), [rows, q]);

  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  useEffect(() => { setPage(0); }, [q, perPage]);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <ModuleHeader icon={<ClassIcon sx={{ fontSize: 28 }} />}
        title="Classes" subtitle="Define classes, grades and class teachers"
        actionLabel="New Class" actionIcon={<AddIcon />} onAction={openNew} />

      {/* Compact toolbar — stats and search share a single row so the
          table starts higher up the page. Chips instead of big stat
          tiles reclaim the vertical space the tiles used to waste. */}
      <Card sx={{ mb: 1.5, p: 1.2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
            <Chip icon={<ClassIcon sx={{ fontSize: 16 }} />}
              label={`${stats.total} total`}
              color="primary" sx={{ fontWeight: 800 }} />
            <Chip icon={<ActiveIcon sx={{ fontSize: 16 }} />}
              label={`${stats.active} active`}
              color="success" variant="outlined" sx={{ fontWeight: 800 }} />
            <Chip icon={<EditIcon sx={{ fontSize: 16 }} />}
              label={`${stats.withTeacher} with teacher`}
              variant="outlined" sx={{ fontWeight: 800 }} />
            <Tooltip title="Learners on the roll across every class">
              <Chip label={`${stats.learners} students`}
                variant="outlined" sx={{ fontWeight: 800 }} />
            </Tooltip>
            {stats.empty > 0 && (
              <Tooltip title="Classes with nobody in them — these are the ones that can be deleted">
                <Chip label={`${stats.empty} empty`}
                  color="warning" variant="outlined" sx={{ fontWeight: 800 }} />
              </Tooltip>
            )}
          </Stack>
          <TextField fullWidth size="small" placeholder="Search class or teacher…"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        {loading ? <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <TableContainer>
              <Table size="small" sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 800, fontSize: 11, letterSpacing: 0.5,
                  textTransform: 'uppercase', color: 'text.secondary',
                  bgcolor: 'grey.50', py: 1,
                  borderBottom: '2px solid', borderColor: 'divider',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  py: 0.8, fontSize: 13,
                },
              }}>
                <TableHead><TableRow>
                  <TableCell sx={{ width: 72 }}>Order</TableCell>
                  <TableCell sx={{ width: 52 }}></TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell sx={{ width: 90 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 96 }}>Students</TableCell>
                  <TableCell>Class Teacher</TableCell>
                  <TableCell sx={{ width: 140 }}>Promotes To</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {paged.map((r, i) => {
                    const inactive = !r.is_active;
                    // Up disabled if this is the first row of its
                    // active bucket in the visible list; down disabled
                    // similarly for the last row of that bucket.
                    const sameBucket = (a, b) => !!a.is_active === !!b.is_active;
                    const prev = paged[i - 1];
                    const next = paged[i + 1];
                    const canUp   = prev && sameBucket(prev, r);
                    const canDown = next && sameBucket(next, r);
                    return (
                      <TableRow key={r.trans_code} hover
                        sx={{ opacity: inactive ? 0.55 : 1 }}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.2}>
                            <Tooltip title={inactive ? 'Inactive — reorder disabled' : (canUp ? 'Move up' : 'Already at top')}>
                              <span>
                                <IconButton size="small" disabled={!canUp || inactive}
                                  onClick={() => move(r, 'up')}>
                                  <UpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={inactive ? 'Inactive — reorder disabled' : (canDown ? 'Move down' : 'Already at bottom')}>
                              <span>
                                <IconButton size="small" disabled={!canDown || inactive}
                                  onClick={() => move(r, 'down')}>
                                  <DownIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Avatar sx={{
                            width: 30, height: 30, fontSize: 12, fontWeight: 800,
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                            color: 'primary.main',
                            border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
                          }}>
                            {(r.class?.[0] || '?').toUpperCase()}
                          </Avatar>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {r.class}
                          <Typography component="span" variant="caption"
                            sx={{ ml: 1, color: 'text.disabled', fontFamily: 'monospace' }}>
                            #{r.trans_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={inactive
                            ? 'Inactive — excluded from promotion order. Click to activate.'
                            : 'Active — included in the class order. Click to deactivate.'}>
                            <Chip
                              size="small"
                              icon={inactive ? <InactiveIcon sx={{ fontSize: 14 }} /> : <ActiveIcon sx={{ fontSize: 14 }} />}
                              label={inactive ? 'Inactive' : 'Active'}
                              color={inactive ? 'default' : 'success'}
                              variant={inactive ? 'outlined' : 'filled'}
                              onClick={() => toggleActive(r)}
                              sx={{ fontWeight: 700, cursor: 'pointer', height: 22 }} />
                          </Tooltip>
                        </TableCell>
                        {/* How many learners are actually in this class.
                            An EMPTY one is called out in words rather
                            than shown as a bare 0, because "empty" is
                            the thing being looked for — these are the
                            rows that are safe to delete. A populated
                            class shows its count and the server refuses
                            to delete it. `null` means the count could
                            not be read; showing 0 there would invite
                            somebody to delete a full class. */}
                        <TableCell align="right">
                          {r.student_count == null ? (
                            <Tooltip title="Could not read the student count">
                              <Typography component="span" variant="body2" color="text.disabled">—</Typography>
                            </Tooltip>
                          ) : r.student_count === 0 ? (
                            <Chip size="small" variant="outlined" label="empty"
                              sx={{ height: 20, fontSize: 10.5, fontWeight: 800 }} />
                          ) : (
                            <Typography component="span"
                              sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                              {r.student_count}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: r.class_t && r.class_t !== 'N/A' ? 'text.primary' : 'text.disabled' }}>
                          {r.class_t && r.class_t !== 'N/A' ? r.class_t : '—'}
                        </TableCell>
                        <TableCell sx={{ color: r.next_class ? 'text.primary' : 'text.disabled' }}>
                          {r.next_class || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <ClassIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                      <Typography color="text.disabled" sx={{ fontSize: 13 }}>
                        {q ? 'No classes match your search' : 'No classes yet'}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => { setPP(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rows:"
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
        }}>
          {editing ? <EditIcon /> : <AddIcon />}
          <Typography variant="h6" fontWeight={700}>
            {editing ? `Edit · ${editing.class}` : 'New Class'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Card sx={{ p: 2.5 }}>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField label="Class Name" value={form.class}
                onChange={(e) => setForm({ ...form, class: e.target.value })}
                placeholder="e.g. JHS 2A" required autoFocus />
              <TextField label="Class Teacher" value={form.class_t}
                onChange={(e) => setForm({ ...form, class_t: e.target.value })}
                placeholder="e.g. Mrs. Mensah" />
              <TextField
                select
                label="Promotes To (optional)"
                value={form.next_class || ''}
                onChange={(e) => setForm({ ...form, next_class: e.target.value })}
                helperText="Destination class for Term-1 bill rosters. Leave blank to use sort order. Set the same target on P1A and P1B to make both promote into P2."
              >
                <MenuItem value=""><em>— use sort order —</em></MenuItem>
                {rows
                  .filter(x => x.is_active && x.class !== form.class)
                  .map(x => (
                    <MenuItem key={x.trans_code} value={x.class}>{x.class}</MenuItem>
                  ))}
              </TextField>
              <FormControlLabel
                control={<Switch checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {form.is_active ? 'Active class' : 'Inactive class'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inactive classes (Alumna, Withdrawn) are hidden from the promotion order.
                    </Typography>
                  </Box>
                } />
            </Box>
          </Card>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editing ? <EditIcon /> : <AddIcon />)}>
            {editing ? 'Save Changes' : 'Create Class'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
